/**
 * useStrategyForm Hook
 * 
 * Main hook for managing strategy form state and configuration.
 * Extracted from StrategyWizardDialog.tsx for better maintainability.
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  StrategyFormData,
  StrategyConfig,
  Leg
} from '../../types/strategy';
import {
  DEFAULT_STRATEGY_CONFIG,
  getDefaultPositionalValues,
  getExpiryText,
  getMaxSliderRange,
  computeStrategySignature
} from '../../utils/strategy';

// Form-state initializers — module-level pure functions of the raw strategy so
// they have stable identity (safe to use in `useMemo`/effect deps below).
const getStrategyName = (strategy: any): string =>
  strategy?.strategyName || strategy?.strategy_name || '';

const getStrategyIndex = (strategy: any): string => strategy?.index || 'NIFTY';

const getStrategyLegs = (strategy: any): Leg[] =>
  strategy?.legsArray || strategy?.legs || [];

// Extract config from flat strategy structure for editing mode
const getStrategyConfig = (strategy: any): StrategyConfig => {
  if (!strategy) return DEFAULT_STRATEGY_CONFIG;

  // If strategy has nested config, use it (for backward compatibility)
  if (strategy.config) return strategy.config;

  // Otherwise, extract config fields from flat strategy structure
  return {
    entryTimeHour: strategy.entryTime?.split(':')[0] || DEFAULT_STRATEGY_CONFIG.entryTimeHour,
    entryTimeMinute: strategy.entryTime?.split(':')[1] || DEFAULT_STRATEGY_CONFIG.entryTimeMinute,
    exitTimeHour: strategy.exitTime?.split(':')[0] || DEFAULT_STRATEGY_CONFIG.exitTimeHour,
    exitTimeMinute: strategy.exitTime?.split(':')[1] || DEFAULT_STRATEGY_CONFIG.exitTimeMinute,
    rangeBreakout: strategy.rangeBreakout ?? DEFAULT_STRATEGY_CONFIG.rangeBreakout,
    rangeBreakoutTimeHour: strategy.rangeBreakoutTime?.split(':')[0] || DEFAULT_STRATEGY_CONFIG.rangeBreakoutTimeHour,
    rangeBreakoutTimeMinute: strategy.rangeBreakoutTime?.split(':')[1] || DEFAULT_STRATEGY_CONFIG.rangeBreakoutTimeMinute,
    orbContinueOvernight: strategy.orbContinueOvernight ?? DEFAULT_STRATEGY_CONFIG.orbContinueOvernight,
    moveSlToCost: strategy.moveSlToCost ?? DEFAULT_STRATEGY_CONFIG.moveSlToCost,
    expiryType: strategy.expiryType || DEFAULT_STRATEGY_CONFIG.expiryType,
    productType: strategy.product || DEFAULT_STRATEGY_CONFIG.productType,
    tradingType: strategy.tradingType || DEFAULT_STRATEGY_CONFIG.tradingType,
    intradayExitMode: strategy.intradayExitMode || DEFAULT_STRATEGY_CONFIG.intradayExitMode,
    expiryDayRollover: strategy.expiryDayRollover ?? DEFAULT_STRATEGY_CONFIG.expiryDayRollover,
    entryDays: strategy.entryDays ?? DEFAULT_STRATEGY_CONFIG.entryDays,
    exitDays: strategy.exitDays ?? DEFAULT_STRATEGY_CONFIG.exitDays,
    entryTradingDaysBeforeExpiry: strategy.entryTradingDaysBeforeExpiry ?? DEFAULT_STRATEGY_CONFIG.entryTradingDaysBeforeExpiry,
    exitTradingDaysBeforeExpiry: strategy.exitTradingDaysBeforeExpiry ?? DEFAULT_STRATEGY_CONFIG.exitTradingDaysBeforeExpiry,
    targetProfit: {
      type: strategy.targetProfit?.type || DEFAULT_STRATEGY_CONFIG.targetProfit.type,
      value: strategy.targetProfit?.value ?? DEFAULT_STRATEGY_CONFIG.targetProfit.value,
    },
    mtmStopLoss: {
      type: strategy.mtmStopLoss?.type || DEFAULT_STRATEGY_CONFIG.mtmStopLoss.type,
      value: strategy.mtmStopLoss?.value ?? DEFAULT_STRATEGY_CONFIG.mtmStopLoss.value,
    },
  };
};

interface UseStrategyFormProps {
  basketId: string;
  editingStrategy?: any; // Optional strategy data for editing mode
  onClose: () => void;
}

interface UseStrategyFormReturn {
  // Form data
  strategyName: string;
  setStrategyName: (name: string) => void;
  strategyIndex: string;
  setStrategyIndex: (index: string) => void;
  strategyConfig: StrategyConfig;
  setStrategyConfig: React.Dispatch<React.SetStateAction<StrategyConfig>>;
  legs: Leg[];
  setLegs: React.Dispatch<React.SetStateAction<Leg[]>>;
  
  // UI state
  error: string | null;
  setError: (error: string | null) => void;
  isSubmitting: boolean;
  setIsSubmitting: (submitting: boolean) => void;
  
  // Dialog ref for accessibility
  dialogRef: React.RefObject<HTMLDivElement>;
  
  // Derived values
  expiryText: string;
  maxSliderRange: number;
  defaultPositionalValues: { entryDays: number; exitDays: number };

  /** Value fingerprint of the form as loaded — compare to the live signature for dirty detection. */
  baselineSignature: string;

  // Form data getter
  getFormData: () => StrategyFormData;
}

export const useStrategyForm = ({
  basketId,
  editingStrategy,
  onClose
}: UseStrategyFormProps): UseStrategyFormReturn => {
  // Refs
  const dialogRef = useRef<HTMLDivElement>(null);

  const [strategyName, setStrategyName] = useState(getStrategyName(editingStrategy));
  const [strategyIndex, setStrategyIndex] = useState(getStrategyIndex(editingStrategy));
  const [strategyConfig, setStrategyConfig] = useState<StrategyConfig>(
    getStrategyConfig(editingStrategy)
  );
  const [legs, setLegs] = useState<Leg[]>(getStrategyLegs(editingStrategy));

  // UI state
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form with editing strategy data
  useEffect(() => {
    if (editingStrategy) {
      setStrategyName(getStrategyName(editingStrategy));
      setStrategyIndex(getStrategyIndex(editingStrategy));
      setStrategyConfig(getStrategyConfig(editingStrategy));
      setLegs(getStrategyLegs(editingStrategy));
    }
  }, [editingStrategy]);

  // Value-based fingerprint of the form as initially loaded for this strategy.
  // Derived straight from `editingStrategy` (not from live state), so it is
  // correct on the same render the prop changes — no dependence on the init
  // effect above having flushed. Consumers compare this against the live
  // signature to detect unsaved edits. Recomputed only when the strategy changes.
  const baselineSignature = useMemo(
    () =>
      computeStrategySignature(
        getStrategyName(editingStrategy),
        getStrategyIndex(editingStrategy),
        getStrategyConfig(editingStrategy),
        getStrategyLegs(editingStrategy)
      ),
    [editingStrategy]
  );
  
  // Focus management for accessibility
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    if (dialogRef.current) {
      dialogRef.current.focus();
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);
  
  // Derived values
  const expiryText = getExpiryText(strategyConfig.expiryType);
  const maxSliderRange = getMaxSliderRange(strategyConfig.expiryType);
  const defaultPositionalValues = getDefaultPositionalValues();
  
  // Form data getter
  const getFormData = useCallback((): StrategyFormData => ({
    basketId,
    strategyName: strategyName.trim(),
    index: strategyIndex,
    config: strategyConfig,
    legs: legs.map(leg => ({
      id: leg.id,
      optionType: leg.optionType,
      actionType: leg.actionType,
      strikePrice: leg.strikePrice,
      totalLots: leg.totalLots,
      selectionMethod: leg.selectionMethod,
      premiumOperator: leg.premiumOperator,
      premiumValue: leg.premiumValue,
      straddlePremiumOperator: leg.straddlePremiumOperator,
      straddlePremiumPercentage: leg.straddlePremiumPercentage,
      straddleWidth: leg.straddleWidth,
      underlyingPremiumOperator: leg.underlyingPremiumOperator,
      underlyingPercentage: leg.underlyingPercentage,
      stopLoss: leg.stopLoss,
      targetProfit: leg.targetProfit,
      trailingStopLoss: leg.trailingStopLoss,
      waitAndTrade: leg.waitAndTrade,
      reEntry: leg.reEntry,
      reExecute: leg.reExecute
    }))
  }), [basketId, strategyName, strategyIndex, strategyConfig, legs]);
  
  return {
    // Form data
    strategyName,
    setStrategyName,
    strategyIndex,
    setStrategyIndex,
    strategyConfig,
    setStrategyConfig,
    legs,
    setLegs,
    
    // UI state
    error,
    setError,
    isSubmitting,
    setIsSubmitting,
    
    // Dialog ref
    dialogRef,
    
    // Derived values
    expiryText,
    maxSliderRange,
    defaultPositionalValues,
    baselineSignature,

    // Form data getter
    getFormData
  };
};